import { useState } from "react";

import { DocumentTitleDocument, DocumentTitleNode } from "@corca/tiptap";
import { Extension } from "@tiptap/core";
import { Plugin, Selection } from "@tiptap/pm/state";
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditorState,
} from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { LockEditingButton } from "@/components/doc/left-sidebar/documents/LockEditingButton";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { Placeholder } from "@/components/tiptap/Placeholder";
import { teamPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";

import LockGlyph from "./LockGlyph.svg";
import { isDocSelected } from "./utils/isDocSelected";

import type { DocumentModel } from "@/models/DocumentModel";
import type { DocumentTitleAttrs } from "@corca/tiptap";
import type { NodeViewProps } from "@tiptap/react";

// Keep in sync with the byline row's min-h-[25px] so the pill never reflows content below.
const PILL_CLASS =
  "inline-flex items-center gap-1 h-[25px] pl-1.5 pr-[11px] rounded-full text-sm leading-none select-none";

export const LockedPill = observer(({ doc }: { doc: DocumentModel }) => {
  const t = useTranslations("doc");
  const [isOpen, setIsOpen] = useState(false);

  const uid = doc.id;

  if (!uid || !doc.api.canToggleLock) {
    return (
      <span
        contentEditable={false}
        className={twMerge(PILL_CLASS, "ml-2 bg-gray-100 dark:bg-gray-800 text-gray-500")}
      >
        <LockGlyph aria-hidden="true" />
        {t("documentTitle.locked")}
      </span>
    );
  }

  return (
    <span contentEditable={false} className="ml-2" onMouseDown={(e) => e.stopPropagation()}>
      <Dropdown
        portal
        anchor={{ to: "bottom start", padding: "4px", gap: "4px" }}
        onOpenChange={setIsOpen}
        toggleButtonLabel={t("documentTitle.locked")}
        className={twMerge(
          PILL_CLASS,
          isOpen
            ? "bg-sidebar-button-active-bg text-blue-600"
            : "bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-sidebar-button-hover-bg",
        )}
        toggleButtonComponent={
          <>
            <LockGlyph aria-hidden="true" />
            {t("documentTitle.locked")}
          </>
        }
        items={[
          {
            renderItem: (props) => (
              <LockEditingButton {...props} uid={uid} locked={doc.api.locked} />
            ),
          },
        ]}
      />
    </span>
  );
});

const DocumentTitleRenderer = observer((props: NodeViewProps) => {
  const { node, extension, editor } = props;
  const { doc } = extension.options as { doc: DocumentModel };
  const t = useTranslations("doc");
  const { alignment } = node.attrs as DocumentTitleAttrs;
  const teamTitle = doc.team?.title;
  const teamPermalink = doc.team?.permalink;
  const router = useRouter();

  const isDocumentSelected = useEditorState({
    editor,
    selector: ({ editor: e }) => isDocSelected(e.view),
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (teamPermalink) {
      router.push(teamPageUrl({ teamPermalink }));
    }
  };

  return (
    <NodeViewWrapper>
      <div
        className={twMerge(
          "flex flex-1 flex-col min-w-1 pb-10",
          "text-size-40 font-bold",

          "max-w-content-width w-full mx-auto",
          "sm:max-w-full",
        )}
      >
        <Placeholder
          alignment={alignment}
          node={node}
          text={t("documentTitle.placeholder")}
          isVisibleUnfocused
        >
          <NodeViewContent
            className={twMerge("flex-1", alignment && `text-${alignment}`)}
            // @ts-expect-error: Invalid tiptap types
            as="h1"
          />
        </Placeholder>
        {teamTitle && teamPermalink && (
          <div
            contentEditable={false}
            className={twMerge(
              "flex items-center min-h-[25px] text-sm text-gray-500 font-normal select-none font-sans tracking-tight",
              isDocumentSelected && "select-text",
              alignment && `text-${alignment}`,
            )}
          >
            <button
              type="button"
              className="font-normal font-sans tracking-tight text-left hover:underline"
              onClick={handleClick}
            >
              {teamTitle} · @{teamPermalink}
            </button>
            {doc.api.locked && <LockedPill doc={doc} />}
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
});

export type DocumentTitleOptions = {
  doc: DocumentModel;
  defaultPageTitle?: string;
  onTitleChange?: (newTitle: string) => void;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    documentTitleExtension: {
      /** Replaces the document title text. */
      setDocumentTitle: (title: string) => ReturnType;
    };
  }
}

export const DocumentTitle = Extension.create<DocumentTitleOptions>({
  name: "documentTitleExtension",

  addCommands() {
    return {
      setDocumentTitle:
        (title) =>
        ({ tr, dispatch }) => {
          const titleNode = tr.doc.firstChild;
          if (titleNode?.type.name !== DocumentTitleNode.name) {
            return false;
          }
          if (dispatch) {
            const content = title ? [tr.doc.type.schema.text(title)] : [];
            tr.replaceWith(1, 1 + titleNode.content.size, content);
          }
          return true;
        },
    };
  },

  addExtensions() {
    const { doc } = this.options;
    return [
      DocumentTitleDocument,
      DocumentTitleNode.extend({
        addOptions: () => {
          return { doc };
        },
        addNodeView: () => ReactNodeViewRenderer(DocumentTitleRenderer),
      }),
    ];
  },

  addProseMirrorPlugins() {
    const { defaultPageTitle, doc, onTitleChange } = this.options;

    return [
      // Handle Enter key in document title
      // event.preventDefault() fixes iOS Safari freeze when pressing "Done" keyboard button
      // and since we're prevent default tiptap Enter event, we need to handle it manually
      new Plugin({
        props: {
          handleDOMEvents: {
            keydown(view, event) {
              if (event.key !== "Enter") return false;

              const { state } = view;
              const { selection } = state;
              const titleNode = state.doc.firstChild;

              if (
                titleNode?.type.name === "documentTitle" &&
                selection.from <= titleNode.nodeSize &&
                selection.to <= titleNode.nodeSize
              ) {
                event.preventDefault();

                const { from, to } = selection;
                const titleStart = 1;
                const titleEnd = titleNode.nodeSize - 1;
                const posAfterTitle = titleNode.nodeSize;
                const cursorPosInTitle = from - titleStart;
                const titleText = titleNode.textContent;

                let { tr } = state;

                if (from !== to) {
                  tr = tr.deleteSelection();
                }

                const textAfterCursor = titleText.slice(cursorPosInTitle);

                if (textAfterCursor.length > 0) {
                  tr = tr.delete(from, titleEnd);
                  const paragraphNode = state.schema.nodes.paragraph.create(
                    null,
                    state.schema.text(textAfterCursor),
                  );
                  tr = tr.insert(posAfterTitle - textAfterCursor.length, paragraphNode);
                  const newParagraphStart = posAfterTitle - textAfterCursor.length + 1;
                  tr = tr.setSelection(Selection.near(tr.doc.resolve(newParagraphStart), 1));
                } else {
                  const paragraphNode = state.schema.nodes.paragraph.createAndFill();
                  if (paragraphNode) {
                    tr = tr.insert(posAfterTitle, paragraphNode);
                    tr = tr.setSelection(Selection.near(tr.doc.resolve(posAfterTitle + 1), 1));
                  }
                }

                view.dispatch(tr);
                return true;
              }

              return false;
            },
          },
        },
      }),
      // Allows deleting first line with Backspace only if it's empty
      // (to prevent "destroying" math by merging it into the header)
      new Plugin({
        props: {
          handleKeyDown(view, event) {
            const { anchor, head } = view.state.selection;
            if (anchor !== head) {
              return undefined;
            }
            if (event.code !== "Backspace" && event.code !== "Delete") {
              return undefined;
            }

            const [titleNodePos, bodyNodePos] = doc.editor.tiptap?.$doc.children || [];
            if (!bodyNodePos || !titleNodePos) return undefined;

            const nonEmptyFirstLine =
              bodyNodePos.size > 2 || bodyNodePos.node.type.name !== "paragraph";

            if (!nonEmptyFirstLine) {
              return undefined;
            }

            if (event.code === "Backspace" && anchor === bodyNodePos.pos) {
              return true;
            }
            if (event.code === "Delete" && anchor === titleNodePos.to - 1) {
              return true;
            }
            return undefined;
          },
        },
      }),
      // Insert empty paragraph if there is no paragraph after the title.
      new Plugin({
        appendTransaction: (_transactions, oldState, newState) => {
          if (!this.editor.isInitialized) return null;

          const { doc: newDoc } = newState;
          const { doc: oldDoc } = oldState;
          const { tr } = newState;

          if (oldDoc.childCount > 1 && newDoc.childCount === 1) {
            const paragraphNode = newState.schema.nodes.paragraph.createAndFill();
            if (paragraphNode) {
              tr.insert(newDoc.content.size, paragraphNode);
              return tr;
            }
          }

          return null;
        },
      }),
      // Update document title
      new Plugin({
        view() {
          let previousTitle = "";

          return {
            update(view) {
              const { state } = view;
              const titleNode = state.doc.firstChild;
              if (titleNode?.type.name === "documentTitle") {
                const newTitle = titleNode.textContent.trim();

                // Only update if the title has actually changed
                if (newTitle !== previousTitle) {
                  previousTitle = newTitle;

                  if (newTitle !== "") {
                    document.title = newTitle;
                    onTitleChange?.(newTitle);
                  } else if (defaultPageTitle) {
                    document.title = defaultPageTitle;
                    onTitleChange?.(newTitle);
                  }
                }
              }
            },
          };
        },
      }),
    ];
  },
});
